export interface ExportRenderSize {
  renderWidth: number;
  renderHeight: number;
  renderScale: number;
}

interface ExportRenderSizingOptions {
  maxCanvasPixels?: number;
  maxCanvasDimension?: number;
}

/**
 * Large paper formats rendered at 3x can exceed browser canvas limits,
 * which shows up as content clipped on the right/bottom edge.
 * Keep the highest supersampling factor that stays within a safe canvas budget.
 */
export const MAX_SAFE_EXPORT_CANVAS_PIXELS = 33_554_432;
export const MAX_SAFE_EXPORT_CANVAS_DIMENSION = 8192;

export const getSafeExportRenderSize = (
  outputWidth: number,
  outputHeight: number,
  preferredRenderScale: number,
  options?: ExportRenderSizingOptions,
): ExportRenderSize => {
  const safeOutputWidth = Math.max(1, Math.floor(outputWidth));
  const safeOutputHeight = Math.max(1, Math.floor(outputHeight));
  const safePreferredRenderScale = Math.max(1, preferredRenderScale);
  const maxCanvasPixels = options?.maxCanvasPixels ?? MAX_SAFE_EXPORT_CANVAS_PIXELS;
  const maxCanvasDimension = options?.maxCanvasDimension ?? MAX_SAFE_EXPORT_CANVAS_DIMENSION;

  const maxRenderScaleByPixels = Math.sqrt(maxCanvasPixels / (safeOutputWidth * safeOutputHeight));
  const maxRenderScaleByDimension = maxCanvasDimension / Math.max(safeOutputWidth, safeOutputHeight);
  const boundedRenderScale = Math.max(
    1,
    Math.min(safePreferredRenderScale, maxRenderScaleByPixels, maxRenderScaleByDimension),
  );

  const renderWidth = Math.max(safeOutputWidth, Math.floor(safeOutputWidth * boundedRenderScale));
  const renderHeight = Math.max(safeOutputHeight, Math.floor(safeOutputHeight * boundedRenderScale));

  return {
    renderWidth,
    renderHeight,
    renderScale: Math.min(renderWidth / safeOutputWidth, renderHeight / safeOutputHeight),
  };
};
